import { useEffect, useState } from "react";
import { Button, Form, Row, Col, DatePicker, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";

import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TDealer, TTicket } from "../../../types";

import {
  AddEditDealer,
  GetSpecificDealerData,
} from "../../../utils/api.constant";
import { TResponseModel } from "../../../types/config";
import dayjs from "dayjs";
import RowDisplay from "../../../components/data-display";
import DateUtils from "../../../utils/date";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { dateOnlyFormat } from "../../../utils/constants";

type DrawerProps = {
  ticket?: TTicket;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function WarrantyApproverForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [dealerDetails, setDealerDetails] = useState<TDealer>();

  useEffect(() => {
    fetchSpecificDealer();
  }, [props?.ticket?.dealerId]);

  const fetchSpecificDealer = async () => {
    if (props?.ticket?.dealerId) {
      try {
        const payload = {
          data: {
            id: props?.ticket?.dealerId,
          },
        };

        const response: TResponseModel = await post(
          GetSpecificDealerData,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            const {
              installationDate,
              amcFromDate,
              amcToDate,
              lastPM,
              customerName,
              warrantyDate,
              isAMCActive,
              ...restData
            } = data;
            form.setFieldsValue({
              ...restData,
              installationDate: installationDate
                ? dayjs(installationDate)
                : null,
              amcFromDate: amcFromDate ? dayjs(amcFromDate) : null,
              amcToDate: amcToDate ? dayjs(amcToDate) : null,
              lastPM: lastPM ? dayjs(lastPM) : null,
              warrantyDate: warrantyDate ? dayjs(warrantyDate) : null,
              customerName: customerName,
              isAMCActive,
            });
            setDealerDetails(data);
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
    }
  };

  // Handle on submit event
  const onFinish = async (values: TDealer) => {
    try {
      setIsLoading(true);
      const { warrantyDate, ...restDetails } = dealerDetails as TDealer;

      const payload = {
        data: {
          ...restDetails,
          warrantyDate: DateUtils.getDateStringWithOutTimezone(
            new Date(values.warrantyDate).toDateString()
          ),
          id: props?.ticket?.dealerId,
          isWarrantyApproved: 1,
        },
      };

      const response = await post(AddEditDealer, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resets the district field and fetches the list of cities based on the selected state
   */

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish} // Pass buttonType to onFinish
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Aprrove Warranty
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <RowDisplay
                heading="Dealer Code"
                content={dealerDetails?.dealerCode || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Depot Code"
                content={dealerDetails?.depotCode || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Dealer Name"
                content={dealerDetails?.dealerName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Customer Name"
                content={dealerDetails?.customerName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Dealer Address"
                content={
                  `${dealerDetails?.address}, ${dealerDetails?.districtName} - ${dealerDetails?.zipcode}, ${dealerDetails?.stateName}` ||
                  ""
                }
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Machine Type"
                content={dealerDetails?.machineTypeName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Model"
                content={dealerDetails?.productName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Machine Serial #"
                content={dealerDetails?.machineSerialNo || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Installation Date"
                content={
                  dealerDetails?.installationDate
                    ? DateUtils.format(
                        dealerDetails?.installationDate,
                        dateOnlyFormat
                      )
                    : "-"
                }
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <Form.Item
                name="warrantyDate"
                label="Warranty Date"
                rules={[
                  {
                    required: true,
                    message: "Please select warranty date",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MMM-YYYY"
                  // value={form.getFieldValue("amcFromDate")}
                  placeholder="Select warranty date"
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              Approve
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              onClick={() => {
                props.onCloseDrawer();
                // setButtonType("reject"); // Set button type to "reject"
                // form.submit();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
